import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  constructor(@Inject(DOCUMENT) private document: Document) {
    this.applyThemeConfig();
  }

  themeConfig: any = {
    mode: 'light',
    primaryColor: '#10b3b0',
    secondaryColor: '#ffebeb',
  };

  setThemeConfigToLocalStorage() {
    localStorage.setItem('themeConfig', JSON.stringify(this.themeConfig));
  }

  getThemeConfigFromLocalStorage() {
    let themeConfig = localStorage.getItem('themeConfig');
    if (themeConfig) {
      this.themeConfig = JSON.parse(themeConfig);
    }
  }

  applyThemeConfig() {
    this.getThemeConfigFromLocalStorage();
    this.switchMode(this.themeConfig.mode);
    this.switchColor(this.themeConfig.primaryColor, 'primary');
    this.switchColor(this.themeConfig.secondaryColor, 'secondary');
    // console.log(this.themeConfig);
  }

  switchMode(mode: 'light' | 'dark') {
    let modeLink = this.document.getElementById('app-theme') as HTMLLinkElement;

    if (modeLink) {
      modeLink.href = `${mode}.css`;
    }

    this.themeConfig.mode = mode;
    this.switchColor(this.themeConfig.primaryColor, 'primary');
    this.switchColor(this.themeConfig.secondaryColor, 'secondary');
    this.setThemeConfigToLocalStorage();
  }

  switchColor(color: string, variable: 'primary' | 'secondary' | 'assistant') {
    color = this.getHexColor(color);
    let colorVariables: any =
      this.themeConfig.mode === 'light'
        ? this.generateColorVariablesLight(color, variable)
        : this.generateColorVariablesDark(color, variable);
    let root = this.document.documentElement;
    for (let key in colorVariables) {
      root.style.setProperty(key, colorVariables[key]);
    }

    this.themeConfig[variable + 'Color'] = color;
    this.setThemeConfigToLocalStorage();
  }

  private getHexColor(color: string): string {
    let ctx = document.createElement('canvas').getContext('2d');
    if (!ctx) return color;
    ctx.fillStyle = color;
    return ctx.fillStyle;
  }

  private hexToRgb(hex: string) {
    let r = '0x' + hex[1] + hex[2];
    let g = '0x' + hex[3] + hex[4];
    let b = '0x' + hex[5] + hex[6];

    return `${+r}, ${+g}, ${+b}`;
  }

  private generateColorVariablesLight(
    color: string,
    variable: 'primary' | 'secondary' | 'assistant'
  ) {
    let colorVariables: any = {};

    colorVariables[`--${variable}-50`] = this.tint(color, 5 * 19);
    for (let i = 100; i <= 900; i += 100) {
      if (i === 500) {
        colorVariables[`--${variable}-${i}`] = color;
      } else if (i < 500) {
        colorVariables[`--${variable}-${i}`] = this.tint(
          color,
          (5 - i / 100) * 19
        );
      } else {
        colorVariables[`--${variable}-${i}`] = this.shade(
          color,
          ((i - 500) / 100) * 15
        );
      }
    }
    colorVariables[`--${variable}-rgb`] = this.hexToRgb(color);
    return colorVariables;
  }

  private generateColorVariablesDark(
    color: string,
    variable: 'primary' | 'secondary' | 'assistant'
  ) {
    let colorVariables: any = {};

    colorVariables[`--${variable}-50`] = this.shade(color, 5 * 19);
    for (let i = 100; i <= 900; i += 100) {
      if (i === 500) {
        colorVariables[`--${variable}-${i}`] = color;
      } else if (i < 500) {
        colorVariables[`--${variable}-${i}`] = this.shade(
          color,
          (5 - i / 100) * 19
        );
      } else {
        colorVariables[`--${variable}-${i}`] = this.tint(
          color,
          ((i - 500) / 100) * 15
        );
      }
    }
    return colorVariables;
  }

  public tint = (color: string, weight: number) => {
    return this.mix('#FFFFFF', color, weight);
  };

  public shade = (color: string, weight: number) => {
    return this.mix('#000000', color, weight);
  };

  private mix = (color1: string, color2: string, weight: number) => {
    let d2h = (d: number) => d.toString(16);
    let h2d = (h: string) => parseInt(h, 16);

    let result = '#';
    for (let i = 1; i < 7; i += 2) {
      let color1Part = h2d(color1.substr(i, 2));
      let color2Part = h2d(color2.substr(i, 2));
      let resultPart = d2h(
        Math.floor(color2Part + (color1Part - color2Part) * (weight / 100.0))
      );
      result += resultPart.length === 1 ? '0' + resultPart : resultPart;
    }
    return result;
  };

  getBackgroundColor = (color: any) => {
    let themeConfig: any = localStorage.getItem('themeConfig');
    if (themeConfig) {
      themeConfig = JSON.parse(themeConfig);
    }
    let mode = themeConfig.mode;
    if (mode === 'light') {
      return {
        backgroundColor: this.tint(color, 4 * 19),
        color: color,
        borderColor: color,
      };
    } else {
      return {
        backgroundColor: this.shade(color, 4 * 19),
        color: color,
        borderColor: color,
      };
    }
  };

  getBackgroundColorOnName = (name: string) => {
    let color = this.getColorOnName(name);
    return color;
  };

  getColorOnName = (name: string) => {
    let colors = [
      '#f44336',
      '#e91e63',
      '#9c27b0',
      '#673ab7',
      '#3f51b5',
      '#2196f3',
      '#03a9f4',
      '#00bcd4',
      '#009688',
      '#4caf50',
      '#8bc34a',
      '#cddc39',
      '#ffeb3b',
      '#ffc107',
      '#ff9800',
      '#ff5722',
      '#795548',
      '#9e9e9e',
      '#607d8b',
    ];
    let sum = 0;
    for (let i = 0; i < name.length; i++) {
      sum += name.charCodeAt(i);
    }
    return colors[sum % colors.length];
  };

  lightenHexColor(hexColor: string = '', percent: number = 80): string {
    // Convert hex to RGB
    let r = parseInt(hexColor.substring(1, 3), 16);
    let g = parseInt(hexColor.substring(3, 5), 16);
    let b = parseInt(hexColor.substring(5, 7), 16);

    // Lighten each component by a certain percentage towards 255
    r = Math.min(255, Math.round(r + (255 - r) * (percent / 100)));
    g = Math.min(255, Math.round(g + (255 - g) * (percent / 100)));
    b = Math.min(255, Math.round(b + (255 - b) * (percent / 100)));

    // Convert RGB back to hex
    const toHex = (c: number) => {
      const hex = c.toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };

    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }

  applyFontFamily(fontFamily: string) {
    let root = this.document.documentElement;
    root.style.setProperty('--font-family', fontFamily);
  }
}
